// src/components/Warehouses/Mintsoft/MintsoftWarehouseDetail.js
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Package,
  Home,
  RefreshCw,
  Search,
  AlertTriangle,
  CheckCircle,
  Warehouse,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Info,
  Tag,
  Box,
  Shield
} from 'lucide-react';

const MintsoftWarehouseDetail = () => {
  const { warehouseId } = useParams();
  const [inventory, setInventory] = useState([]);
  const [warehouseInfo, setWarehouseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    total_items: 0,
    total_skus: 0,
    low_stock_items: 0,
    out_of_stock_items: 0,
    pre_orderable_items: 0,
    bundle_items: 0,
    reserved_stock_total: 0
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // all, in_stock, low_stock, pre_orderable
  const [sortBy, setSortBy] = useState('sku'); // sku, total_stock_level, level

  // Fetch Mintsoft warehouse inventory
  const fetchMintsoftInventory = async () => {
    try {
      setLoading(true);
      setError('');
      console.log(`🔄 Loading Mintsoft inventory for warehouse ${warehouseId}...`);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard/warehouse/${warehouseId}/inventory`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Mintsoft warehouse ${warehouseId} inventory`);
      }

      const data = await response.json();
      console.log('Mintsoft warehouse data:', data);

      const warehouseData = data.warehouse_inventory;
      if (warehouseData && warehouseData.success) {
        setWarehouseInfo(warehouseData.warehouse);

        // Filter Mintsoft items with TotalStockLevel > 0 (using MintsoftParser fields)
        const inventoryItems = warehouseData.inventory.filter(item => item.total_stock_level > 0);
        setInventory(inventoryItems);

        // Calculate Mintsoft-specific statistics
        const totalItems = inventoryItems.reduce((sum, item) => sum + item.total_stock_level, 0);
        const lowStockItems = inventoryItems.filter(item => item.is_low_stock).length;
        const outOfStockItems = inventoryItems.filter(item => item.is_out_of_stock).length;
        const preOrderableItems = inventoryItems.filter(item => item.pre_orderable).length;
        const bundleItems = inventoryItems.filter(item => item.is_bundle).length;
        const reservedStockTotal = inventoryItems.reduce((sum, item) => sum + item.reserved_stock, 0);

        setStats({
          total_items: totalItems,
          total_skus: inventoryItems.length,
          low_stock_items: lowStockItems,
          out_of_stock_items: outOfStockItems,
          pre_orderable_items: preOrderableItems,
          bundle_items: bundleItems,
          reserved_stock_total: reservedStockTotal
        });

        console.log(`✅ Loaded ${inventoryItems.length} Mintsoft items with TotalStockLevel > 0`);
      } else {
        throw new Error(warehouseData?.error?.message || 'Failed to load Mintsoft warehouse data');
      }

    } catch (error) {
      console.error('❌ Error fetching Mintsoft inventory:', error);
      setError('Failed to load Mintsoft inventory: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort Mintsoft inventory
  const getFilteredAndSortedInventory = () => {
    let filtered = inventory.filter(item => {
      // Search filter
      const matchesSearch = item.sku.toLowerCase().includes(searchTerm.toLowerCase());

      // Mintsoft-specific stock status filter
      let matchesStock = true;
      switch (stockFilter) {
        case 'in_stock':
          matchesStock = !item.is_low_stock && !item.is_out_of_stock;
          break;
        case 'low_stock':
          matchesStock = item.is_low_stock;
          break;
        case 'pre_orderable':
          matchesStock = item.pre_orderable;
          break;
        case 'all':
        default:
          matchesStock = true;
      }

      return matchesSearch && matchesStock;
    });

    // Sort inventory using Mintsoft fields
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'total_stock_level':
          return b.total_stock_level - a.total_stock_level;
        case 'level':
          return b.available_level - a.available_level;
        case 'last_updated':
          return new Date(b.last_updated || 0) - new Date(a.last_updated || 0);
        case 'sku':
        default:
          return a.sku.localeCompare(b.sku);
      }
    });

    return filtered;
  };

  const filteredInventory = getFilteredAndSortedInventory();

  // Get Mintsoft-specific stock status badge
  const getMintsoftStockStatusBadge = (item) => {
    if (item.is_out_of_stock) {
      return {
        text: 'Out of Stock',
        color: '#dc2626',
        bg: '#fef2f2',
        icon: <Minus size={12} />
      };
    } else if (item.is_low_stock) {
      return {
        text: 'Low Stock',
        color: '#d97706',
        bg: '#fef3c7',
        icon: <TrendingDown size={12} />
      };
    } else {
      return {
        text: 'In Stock',
        color: '#16a34a',
        bg: '#f0fdf4',
        icon: <TrendingUp size={12} />
      };
    }
  };

  useEffect(() => {
    fetchMintsoftInventory();
  }, [warehouseId]);

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div className="error-message">
            <AlertTriangle size={24} />
            <div>
              <h3>Error Loading Mintsoft Warehouse</h3>
              <p className="error-text">{error}</p>
              <button
                onClick={fetchMintsoftInventory}
                className="btn-primary"
                style={{marginTop: '1rem', width: 'auto'}}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem'}}>
              <Link to="/dashboard" className="btn-secondary" style={{minWidth: 'auto', padding: '0.5rem'}}>
                <ArrowLeft size={16} />
              </Link>
              <h1 className="header-title">
                {warehouseInfo?.name || `Warehouse ${warehouseId}`} - Mintsoft
              </h1>
            </div>
            <p className="header-subtitle">
              {warehouseInfo?.location} • Mintsoft Inventory Management
            </p>
          </div>

          <div className="header-actions">
            <Link to="/dashboard" className="btn-secondary">
              <Home size={16} />
              <span>Dashboard</span>
            </Link>

            <button
              onClick={fetchMintsoftInventory}
              disabled={loading}
              className="btn-secondary"
            >
              <RefreshCw size={16} className={loading ? 'loading-spinner' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-main">
        {/* Mintsoft Statistics Cards */}
        <div className="cards-grid">
          <div className="dashboard-card">
            <div className="card-content">
              <div className="card-info">
                <p className="card-title">Total Stock</p>
                <p className="card-value">{stats.total_items.toLocaleString()}</p>
                <p className="card-subtitle">{stats.total_skus} unique SKUs</p>
              </div>
              <div className="card-icon icon-blue">
                <Package size={24} />
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-content">
              <div className="card-info">
                <p className="card-title">Reserved Stock</p>
                <p className="card-value">{stats.reserved_stock_total.toLocaleString()}</p>
                <p className="card-subtitle">Currently reserved</p>
              </div>
              <div className="card-icon icon-yellow">
                <Shield size={24} />
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-content">
              <div className="card-info">
                <p className="card-title">Pre-Orderable</p>
                <p className="card-value">{stats.pre_orderable_items}</p>
                <p className="card-subtitle">Items available for pre-order</p>
              </div>
              <div className="card-icon icon-green">
                <Tag size={24} />
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-content">
              <div className="card-info">
                <p className="card-title">Bundle Items</p>
                <p className="card-value">{stats.bundle_items}</p>
                <p className="card-subtitle">Product bundles</p>
              </div>
              <div className="card-icon icon-blue">
                <Box size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Mintsoft Filters */}
        <div className="dashboard-card" style={{marginBottom: '2rem'}}>
          <div style={{display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap'}}>
            {/* Search Input */}
            <div style={{position: 'relative', minWidth: '200px'}}>
              <Search size={16} style={{position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}} />
              <input
                type="text"
                placeholder="Search Mintsoft SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.5rem 0.5rem 2.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            {/* Mintsoft Stock Filter */}
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All Items</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="pre_orderable">Pre-Orderable</option>
            </select>

            {/* Mintsoft Sort Options */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="sku">Sort by SKU</option>
              <option value="total_stock_level">Sort by Total Stock</option>
              <option value="level">Sort by Available Level</option>
              <option value="last_updated">Sort by Last Updated</option>
            </select>
          </div>
        </div>

        {/* Mintsoft Inventory Table */}
        <div className="dashboard-card">
          <div className="section-title" style={{marginBottom: '1.5rem'}}>
            <Package size={20} />
            Mintsoft Inventory ({filteredInventory.length})
          </div>

          {loading ? (
            <div style={{textAlign: 'center', padding: '2rem'}}>
              <div className="loading-spinner" style={{margin: '0 auto'}}></div>
              <p style={{marginTop: '1rem', color: '#6b7280'}}>Loading Mintsoft inventory...</p>
            </div>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>SKU</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Total Stock</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Available</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Reserved</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Status</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Low Stock Level</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Features</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.map((item) => {
                    const statusBadge = getMintsoftStockStatusBadge(item);

                    return (
                      <tr key={item.product_id} style={{borderBottom: '1px solid #f3f4f6'}}>
                        {/* SKU */}
                        <td style={{padding: '0.75rem'}}>
                          <div style={{fontWeight: '500', color: '#1f2937', fontFamily: 'monospace'}}>
                            {item.sku}
                          </div>
                          <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                            Product ID: {item.product_id}
                          </div>
                          <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>
                            Client: {item.client_id}
                          </div>
                        </td>

                        {/* Total Stock (TotalStockLevel) */}
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            fontWeight: '600',
                            color: '#1f2937',
                            fontSize: '1.125rem'
                          }}>
                            {item.total_stock_level.toLocaleString()}
                          </span>
                        </td>

                        {/* Available (Level) */}
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            fontWeight: '500',
                            color: '#374151',
                            fontSize: '1rem'
                          }}>
                            {item.available_level.toLocaleString()}
                          </span>
                        </td>

                        {/* Reserved */}
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            fontWeight: '500',
                            color: item.reserved_stock > 0 ? '#d97706' : '#6b7280',
                            fontSize: '1rem'
                          }}>
                            {item.reserved_stock.toLocaleString()}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: statusBadge.bg,
                            color: statusBadge.color
                          }}>
                            {statusBadge.icon}
                            {statusBadge.text}
                          </span>
                        </td>

                        {/* Low Stock Level */}
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            color: '#6b7280',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}>
                            {item.low_stock_level}
                          </span>
                        </td>

                        {/* Mintsoft Features */}
                        <td style={{padding: '0.75rem'}}>
                          <div style={{display: 'flex', gap: '0.25rem', flexWrap: 'wrap'}}>
                            {item.pre_orderable && (
                              <span style={{
                                fontSize: '0.625rem',
                                padding: '0.125rem 0.25rem',
                                backgroundColor: '#e0f2fe',
                                color: '#0369a1',
                                borderRadius: '3px',
                                fontWeight: '600'
                              }}>
                                PreOrder
                              </span>
                            )}
                            {item.is_bundle && (
                              <span style={{
                                fontSize: '0.625rem',
                                padding: '0.125rem 0.25rem',
                                backgroundColor: '#f3e8ff',
                                color: '#7c3aed',
                                borderRadius: '3px',
                                fontWeight: '600'
                              }}>
                                Bundle
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Last Updated */}
                        <td style={{padding: '0.75rem'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                            <Clock size={12} color="#6b7280" />
                            <span style={{color: '#6b7280', fontSize: '0.875rem'}}>
                              {item.last_updated ? new Date(item.last_updated).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredInventory.length === 0 && !loading && (
                <div className="empty-state">
                  <Package size={48} style={{opacity: 0.5, margin: '0 auto 0.75rem'}} />
                  <p>No Mintsoft inventory items found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MintsoftWarehouseDetail;